import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  LinearProgress,
  Alert,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Fab,
  Snackbar,
  Skeleton,
  Menu
} from '@mui/material';
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import {
  Search,
  FilterList,
  Download,
  Preview,
  Refresh,
  Delete,
  CheckCircle,
  Schedule,
  PlayArrow,
  Cancel,
  ZoomIn,
  PhotoLibrary,
  GetApp,
  Close,
  Share,
  ArrowBack,
  CollectionsBookmark,
  KeyboardArrowUp,
} from '@mui/icons-material';
import merchantAPI, { aiGenerationAPI, catalogAPI } from '../../../services/merchantAPI';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import DynamicMerchantTabs from '../Layout/DynamicMerchantTabs';
import MerchantWelcomeSection from '../Layout/MerchantWelcomeSection';
import { useNavigate, useLocation } from 'react-router-dom';
import { parseAddToCatalogState } from '../Catalog/catalogTheme';
import { API_URLS } from '../../../config/apiConfig';
import { AutoCloseAlert } from '../../Common';
import FlutterServicesAPI from '../../../services/flutterServices';
import { getThumbnailUrl as getSecureThumbnailUrl } from '../../../utils/imageUrlUtils';
import {
  getGenerationDownloadBaseName,
  getGenerationDownloadFileName,
  createGalleryZipDownloadNames,
  createGalleryPdfCatalogueFileName
} from '../../../utils/generationDownloadFilename';
import SecureImage from '../../Common/SecureImage';
import SecureVideo from '../../Common/SecureVideo';
import GenerationPreviewDialog from '../History/GenerationPreviewDialog';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import { getBaseUrl } from '../../../utils/environmentUtils';
import ShareSocialButton from '../../Common/ShareSocialButton';
import Cookies from 'js-cookie';
import GenerationInfoPopup from './GenerationInfoPopup';
import AddToCatalogDialog from './AddToCatalogDialog';
import ReelCreationDialog from './ReelCreationDialog';
import { useTheme, useMediaQuery } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

// Icon button style for the card action bar
const cardIconBtnStyle = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  border: '1.5px solid #979797',
  boxShadow: '0px 3px 3px 0px rgba(0,0,0,0.07)',
  bgcolor: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    bgcolor: '#f5f5f5',
    boxShadow: '0px 4px 6px 0px rgba(0,0,0,0.12)',
  },
};

const Gallery = () => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { merchant } = useMerchantAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const addToCatalogCtx = parseAddToCatalogState(location.state);
  const isCatalogPickMode = Boolean(addToCatalogCtx?.catalogId);
  const existingInCatalogSet = useMemo(
    () => new Set(addToCatalogCtx?.existingGenerationIds || []),
    [addToCatalogCtx]
  );
  const [addingToCatalog, setAddingToCatalog] = useState(false);
  // State for gallery data
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 4 : 5);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const sentinelRef = useRef(null);
  const [showNextSkeleton, setShowNextSkeleton] = useState(false);

  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const filterPanelRef = useRef(null);

  // Advanced Filter States (applied)
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTags, setSearchTags] = useState([]); // multi-tag search
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateError, setDateError] = useState('');

  // Temporary Filter States (while filter panel is open)
  const [tempSearchTerm, setTempSearchTerm] = useState('');
  const [tempStatusFilter, setTempStatusFilter] = useState('');
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');

  // Tag search / autocomplete state
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tagSuggestionsLoading, setTagSuggestionsLoading] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const tagSuggestCacheRef = useRef(new Map());
  const tagSuggestAbortRef = useRef(null);
  const tagInputRef = useRef(null);
  const tagDropdownRef = useRef(null);

  // Filter state
  const [typeFilter, setTypeFilter] = useState(''); // 'image', 'video', or ''

  // Selection state
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedItemsCache, setSelectedItemsCache] = useState({});

  // Dialog for Add to Catalog
  const [addToCatalogDialogOpen, setAddToCatalogDialogOpen] = useState(false); // Cache item data by ID for pagination persistence
  const [selectAll, setSelectAll] = useState(false);

  // Dialog state
  const [previewDialog, setPreviewDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [zoomDialog, setZoomDialog] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [infoDialog, setInfoDialog] = useState(false);
  const [infoItem, setInfoItem] = useState(null);

  // Download state
  const [downloadingFiles, setDownloadingFiles] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Dynamic brand logo dialog state for PDF download
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [brandLogos, setBrandLogos] = useState([]);
  const [selectedLogoId, setSelectedLogoId] = useState('default');
  const [logosLoading, setLogosLoading] = useState(false);

  // Add Brand Logo from Gallery dialog
  const [isAddingNewLogo, setIsAddingNewLogo] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newLogoFile, setNewLogoFile] = useState(null);
  const [newLogoUploading, setNewLogoUploading] = useState(false);
  const [newLogoError, setNewLogoError] = useState('');
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(false);

  // Scroll-to-top button visibility
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // More Menu state
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const openMore = Boolean(moreAnchorEl);
  const handleMoreClick = (event) => {
    setMoreAnchorEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  }

  // Reel creation state
  const [reelDialogOpen, setReelDialogOpen] = useState(false);
  const [reelVideoIds, setReelVideoIds] = useState([]);
  const [reelLimitOpen, setReelLimitOpen] = useState(false);   // ">4 videos" popup
  const [reelSelectedCount, setReelSelectedCount] = useState(0);

  const REEL_MAX_VIDEOS = 4;

  const isGracePeriod = merchant?.subscription_info?.status === 'GRACE';

  const handleOpenReelDialog = () => {
    if (!canAddToReels) {
      setError('You do not have permission to create reels');
      handleMoreClose();
      return;
    }
    if (isGracePeriod) {
      setError('Your subscription is in grace period. Please renew to create reels.');
      handleMoreClose();
      return;
    }
    // TEST MODE: allow images and/or videos (images become 5s stills with a dissolve).
    const mediaIds = selectedItems.filter((id) => {
      const item = galleryItems.find(i => i.id === id) || selectedItemsCache[id];
      const ft = item && getFileType(item);
      return ft === 'video' || ft === 'image';
    });
    if (mediaIds.length === 0) {
      setError('Please select at least one image or video to create a reel');
      handleMoreClose();
      return;
    }
    // At most 4 items — show a popup and abort if more are selected.
    if (mediaIds.length > REEL_MAX_VIDEOS) {
      handleMoreClose();
      setReelSelectedCount(mediaIds.length);
      setReelLimitOpen(true);
      return;
    }
    setReelVideoIds(mediaIds);
    setReelDialogOpen(true);
    handleMoreClose();
  };

  const { hasPermission, loading: permissionLoading } = useMerchantPermissions();
  const canDownload = hasPermission('Gallery', 'download');
  const canDownloadPdf = hasPermission('PDF Catalogue', 'view');
  const canShare = hasPermission('Gallery', 'share');
  const canPreview = hasPermission('Gallery', 'preview');
  const canDelete = hasPermission('Gallery', 'delete');
  const canAddToReels = hasPermission('Reels', 'view');
  const canAddToWebCatalogue = hasPermission('Catalog Management', 'create');

  // Constants for new UI
  const PRIMARY_GRADIENT = "linear-gradient(90deg, #A864ED 0%, #F5B760 100%)";
  const DISABLED_GRADIENT = "linear-gradient(90deg, #9A6AD9 0%, #F6DD59 100%)";
  const DOWNLOAD_SELECTED_ICON = "/assets/img/icons/download_selected.svg";
  const SHARE_SELECTED_ICON = "/assets/img/icons/share_selected.svg";
  const MORE_ICON = "/assets/img/icons/more_icon.svg";

  // More menu options: pdf_catalogue gates PDF export, download gates ZIP.
  const moreOptions = [
    {
      label: 'Pdf Catalogue',
      icon: "/assets/img/icons/pdf.svg",
      onClick: () => { handlePdfClick(); handleMoreClose(); },
      disabled: !canDownloadPdf || selectedItems.length === 0 || downloadingPdf || downloadingZip || downloadingFiles,
      showComingSoon: false
    },
    { label: 'Web Catalogue', icon: "/assets/img/icons/web.svg", onClick: () => { handleMoreClose(); setAddToCatalogDialogOpen(true); }, disabled: !canAddToWebCatalogue || selectedItems.length === 0 || isGracePeriod, showComingSoon: false, showNew: true },
    { label: 'Reels', icon: "/assets/img/icons/reel.svg", onClick: handleOpenReelDialog, disabled: !canAddToReels || selectedItems.length === 0 || isGracePeriod, showComingSoon: false, showNew: true },
    {
      label: 'Download Zip',
      icon: "/assets/img/icons/download_zip.svg",
      onClick: () => { handleDownloadZip(); handleMoreClose(); },
      disabled: !canDownload || selectedItems.length === 0 || downloadingZip || downloadingPdf || downloadingFiles,
      showComingSoon: false
    },
  ];

  // Browser hints for grid thumbnails — passed through SecureImage to <img> (offscreen decode priority)
  const GALLERY_THUMB_IMG_HINTS = { loading: 'lazy', decoding: 'async' };

  const btnStyle = {
    background: PRIMARY_GRADIENT,
    color: 'white',
    borderRadius: '10px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: { xs: '14px', md: '16px' },
    height: { xs: '45px', md: '50px' },
    minWidth: { xs: 'auto', md: '140px' },
    px: 3,
    transition: 'all 0.3s ease',
    '&:hover': {
      background: PRIMARY_GRADIENT,
      opacity: 0.9,
      boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
    },
    '&:disabled': {
      background: '#E0E0E0',
      color: '#A0A0A0'
    }
  };

  // Add click-outside listener for filter panel
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        filterPanelOpen &&
        filterPanelRef.current &&
        !filterPanelRef.current.contains(event.target) &&
        !event.target.closest('.MuiPickersPopper-root') &&
        !event.target.closest('.MuiPopover-root') &&
        !event.target.closest('.MuiModal-root') &&
        !event.target.closest('.MuiDialog-root')
      ) {
        setFilterPanelOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filterPanelOpen]);

  // Date validation function
  const validateDates = (start, end) => {
    const today = new Date();
    const todayStr = today.getFullYear() + '-' +
      String(today.getMonth() + 1).padStart(2, '0') + '-' +
      String(today.getDate()).padStart(2, '0');

    if (!start && !end) return '';

    if (start && start > todayStr) {
      return "Start date cannot be in the future";
    }
    if (end && end > todayStr) {
      return "End date cannot be in the future";
    }
    if (start && end && start > end) {
      return "Start date cannot be after end date";
    }
    return '';
  };

  // Function to sync temporary states with actual filter states
  const syncTempFilters = () => {
    setTempStatusFilter(statusFilter);
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setTempSearchTerm(searchTerm);
  };

  const applyFilters = () => {
    const error = (tempStartDate || tempEndDate) ? validateDates(tempStartDate, tempEndDate) : '';
    setDateError(error);
    if (error) return;

    setStatusFilter(tempStatusFilter);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    // Note: searchTerm is driven by the tag chip input, not the filter panel
    setFilterPanelOpen(false);
  };

  // Gallery items state is managed internally

  // Load gallery data (append-aware)
  const loadGallery = async (opts = { append: false, targetPage: null }) => {
    const nextPage = opts.targetPage !== null && opts.targetPage !== undefined ? opts.targetPage : page;
    if (opts.append) {
      setIsFetchingMore(true);
    } else {
      setLoading(true);
    }
    try {
      // console.log('API getGallery:start', { page: nextPage + 1, per_page: rowsPerPage, type: typeFilter, append: opts.append });
      const params = {
        page: nextPage + 1,
        per_page: rowsPerPage,
        type: typeFilter,
        // Use `tag` param for chip-based tag search so it only matches user-defined
        // custom tags, not system fields like material_name or category_name.
        tag: searchTerm.trim() || undefined,
        status: statusFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined
      };
      const response = await aiGenerationAPI.getGallery(params);
      const items = response?.data?.items || [];
      const total = response?.data?.total || 0;
      setTotalCount(total);
      const computedHasMore = (nextPage + 1) * rowsPerPage < total;
      setHasMore(computedHasMore);
      // console.log('API getGallery:done', { page: nextPage + 1, received: items.length, total, hasMore: computedHasMore });

      if (opts.append) {
        setGalleryItems(prev => {
          // avoid duplicates if filters changed fast
          const prevIds = new Set(prev.map(i => i.id));
          const merged = [...prev];
          for (const it of items) {
            if (!prevIds.has(it.id)) merged.push(it);
          }
          return merged;
        });
      } else {
        setGalleryItems(items);
      }
    } catch (error) {
      console.error('Failed to load gallery:', error);
      setError('Failed to load gallery items');
    } finally {
      if (opts.append) {
        setIsFetchingMore(false);
        setShowNextSkeleton(false);
      } else {
        setLoading(false);
      }
    }
  };

  // After a reel is created, surface it WITHOUT collapsing the grid.
  // Fetch the newest page and prepend only items not already loaded, so anything
  // the user scrolled to load (infinite scroll) stays in place.
  const prependNewGalleryItems = async () => {
    try {
      const params = {
        page: 1,
        per_page: rowsPerPage,
        type: typeFilter,
        tag: searchTerm.trim() || undefined,
        status: statusFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      const response = await aiGenerationAPI.getGallery(params);
      const items = response?.data?.items || [];
      setTotalCount(response?.data?.total || 0);
      setGalleryItems(prev => {
        const prevIds = new Set(prev.map(i => i.id));
        const fresh = items.filter(it => !prevIds.has(it.id));
        return fresh.length ? [...fresh, ...prev] : prev;
      });
    } catch (e) {
      console.error('Failed to refresh gallery after reel:', e);
    }
  };

  // Close tag dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        tagDropdownRef.current && !tagDropdownRef.current.contains(e.target) &&
        tagInputRef.current && !tagInputRef.current.contains(e.target)
      ) {
        setShowTagDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch tag suggestions as user types
  useEffect(() => {
    const query = tagInput.trim();
    if (query.length < 1) {
      setTagSuggestions([]);
      setShowTagDropdown(false);
      return;
    }
    const normalizedQuery = query.toLowerCase();
    const cached = tagSuggestCacheRef.current.get(normalizedQuery);
    if (cached) {
      const filtered = cached.filter(t => !searchTags.includes(t));
      setTagSuggestions(filtered);
      setShowTagDropdown(filtered.length > 0);
      return;
    }
    const debounceId = setTimeout(async () => {
      try {
        setTagSuggestionsLoading(true);
        if (tagSuggestAbortRef.current) tagSuggestAbortRef.current.abort();
        const controller = new AbortController();
        tagSuggestAbortRef.current = controller;
        const { data } = await aiGenerationAPI.getSearchSuggestions(
          { q: query, limit: 12 },
          { signal: controller.signal }
        );
        const fetched = Array.isArray(data?.suggestions) ? data.suggestions : [];
        tagSuggestCacheRef.current.set(normalizedQuery, fetched);
        const filtered = fetched.filter(t => !searchTags.includes(t));
        setTagSuggestions(filtered);
        setShowTagDropdown(filtered.length > 0);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setTagSuggestions([]);
      } finally {
        setTagSuggestionsLoading(false);
      }
    }, 200);
    return () => {
      clearTimeout(debounceId);
      if (tagSuggestAbortRef.current) { tagSuggestAbortRef.current.abort(); tagSuggestAbortRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagInput, searchTags]);

  // Helpers for tag search
  const addTagToSearch = (tagSuggestion) => {
    const tag = typeof tagSuggestion === 'string' ? tagSuggestion : tagSuggestion.label;
    const trimmed = tag.trim();
    if (!trimmed || searchTags.includes(trimmed)) return;
    const newTags = [...searchTags, trimmed];
    setSearchTags(newTags);
    setSearchTerm(newTags.join(','));
    setTagInput('');
    setTagSuggestions([]);
    setShowTagDropdown(false);
  };

  const removeTagFromSearch = (tag) => {
    const newTags = searchTags.filter(t => t !== tag);
    setSearchTags(newTags);
    setSearchTerm(newTags.join(','));
  };

  // Load gallery on filter or rowsPerPage changes (reset to first page)
  useEffect(() => {
    setPage(0);
    setHasMore(true);
    loadGallery({ append: false, targetPage: 0 });
  }, [rowsPerPage, typeFilter, statusFilter, searchTerm, startDate, endDate]); // searchTerm is derived from searchTags

  // Load when page changes due to infinite scroll (append)
  useEffect(() => {
    if (page === 0) return; // already loaded via above effect
    loadGallery({ append: true, targetPage: page });
  }, [page]);

  // Handle type filter
  const handleTypeFilter = (event) => {
    setTypeFilter(event.target.value);
    setPage(0); // Reset to first page
  };

  // Handle pagination (infinite)
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMore && !isFetchingMore && !loading && !showNextSkeleton) {
        // Show skeleton immediately, then request next page on next tick (was 1000ms — too slow on slow networks)
        setShowNextSkeleton(true);
        setTimeout(() => {
          setPage((prev) => prev + 1);
        }, 0);
      }
    }, { root: null, rootMargin: '200px', threshold: 0.0 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isFetchingMore, loading, page, showNextSkeleton]);

  // Add selected gallery items to catalog from dialog
  const handleDialogAdd = async (catalogId) => {
    if (!catalogId || selectedItems.length === 0) return;
    setAddingToCatalog(true);
    setError('');
    try {
      const idsToAdd = selectedItems.filter(id => !existingInCatalogSet.has(id));
      if (idsToAdd.length > 0) {
        await catalogAPI.addItems(catalogId, idsToAdd);
      }
      setSuccess(`Added ${idsToAdd.length} item${idsToAdd.length !== 1 ? 's' : ''} to catalog`);
      setAddToCatalogDialogOpen(false);
      setSelectedItems([]);

      // If we came from a specific catalog, and we just added to it, we can return to it.
      // Otherwise, stay on gallery or redirect to the chosen catalog.
      navigate(addToCatalogCtx?.returnPath || '/catalogs', {
        replace: true,
        state: {
          activeCatalogId: catalogId,
        },
      });
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to add items to catalog');
    } finally {
      setAddingToCatalog(false);
    }
  };

  const handleBackToCatalogs = () => {
    navigate(addToCatalogCtx?.returnPath || '/catalogs', {
      state: addToCatalogCtx?.catalogId
        ? { activeCatalogId: addToCatalogCtx.catalogId }
        : {},
    });
  };

  // Handle item selection
  const handleItemSelect = (itemId) => {
    if (isCatalogPickMode && existingInCatalogSet.has(itemId)) return;

    // Cache the item data if selecting (find in galleryItems or use existing cache)
    const item = galleryItems.find(i => i.id === itemId);
    if (item) {
      setSelectedItemsCache(prev => ({ ...prev, [itemId]: item }));
    }

    setSelectedItems(prev => {
      const newSelectedItems = prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId];

      // Update selectAll state based on whether all items are selected
      const allSelected = newSelectedItems.length === galleryItems.length;
      setSelectAll(allSelected);

      // Clean up cache for deselected items
      if (prev.includes(itemId)) {
        setSelectedItemsCache(prevCache => {
          const newCache = { ...prevCache };
          delete newCache[itemId];
          return newCache;
        });
      }

      return newSelectedItems;
    });
  };

  // Handle select all
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const ids = galleryItems
        .map(item => item.id)
        .filter(id => !(isCatalogPickMode && existingInCatalogSet.has(id)));
      setSelectedItems(ids);

      // Populate cache for all selected items
      const newCacheItems = {};
      galleryItems.forEach(item => {
        newCacheItems[item.id] = item;
      });
      setSelectedItemsCache(prev => ({ ...prev, ...newCacheItems }));

      setSelectAll(true);
    } else {
      setSelectedItems([]);
      setSelectAll(false);
      // We don't necessarily clear cache on deselect all to avoid accidental data loss 
      // if some items were manually selected before? 
      // Actually, clearing it for items that are being DEselected is fine.
      // But clearing everything is safer for memory.
      setSelectedItemsCache({});
    }
  };

  // Handle preview
  const handlePreview = async (item) => {
    try {
      await aiGenerationAPI.checkPreviewLimit(item.id);
    } catch (err) {
      if (err?.response?.status === 429) return;
    }
    setSelectedItem(item);
    setPreviewDialog(true);
  };

  const handleRegenerateFromInfo = (generation) => {
    if (!generation?.id) return;

    try {
      // Keep regenerate payload mapping aligned with history flow.
      const prefillData = {
        category_id: generation.category_id || generation.category?.id || generation.jewelry_category?.id,
        material_id: generation.material_id || generation.material?.id || generation.jewelry_material?.id,
        model_type_id: generation.model_type_id || generation.model_type?.id || generation.bust_type?.id,
        output_format_id: generation.output_format_id || generation.output_format?.id,
        gender_option_id: generation.gender_option_id || generation.gender_option?.id,
        ethnicity_id: generation.ethnicity_id || generation.ethnicity?.id,
        clothing_type_id: generation.clothing_type_id || generation.clothing_type?.id,
        bust_color_id: generation.bust_color_id || generation.bust_color?.id,
        bust_finish_id: generation.bust_finish_id || generation.bust_finish?.id,
        media_quality: generation.media_quality?.name || generation.media_quality?.display_name || (typeof generation.media_quality === 'string' ? generation.media_quality : generation.output_quality || ''),
        has_watermark: generation.has_watermark,
        has_brand_logo: generation.has_brand_logo || (generation.brand_logo_id ? true : false),
        brand_logo_id: generation.brand_logo_id,
        model_face_image_id: generation.model_face_image_id || generation.selected_ai_model?.id || (typeof generation.selected_ai_model === 'string' ? generation.selected_ai_model : ''),
        custom_tags: Array.isArray(generation.custom_tags)
          ? generation.custom_tags.join(',')
          : (generation.custom_tags || ''),
        caption_text_1: generation.caption_text_1 || '',
        caption_text_2: generation.caption_text_2 || ''
      };

      const searchParams = new URLSearchParams();
      searchParams.append('regenerate', 'true');
      searchParams.append('from_generation_id', generation.id);

      Object.entries(prefillData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          searchParams.append(key, value);
        }
      });

      setInfoDialog(false);
      navigate(`/generate?${searchParams.toString()}`);
    } catch (e) {
      setError('Failed to regenerate with same settings. Please try again.');
    }
  };

  const handleCustomTagsSaved = useCallback((generationId, savedCommaString) => {
    setInfoItem((prev) =>
      prev && prev.id === generationId ? { ...prev, custom_tags: savedCommaString } : prev
    );
    setGalleryItems((prev) =>
      prev.map((g) => (g.id === generationId ? { ...g, custom_tags: savedCommaString } : g))
    );
    setSelectedItemsCache((prev) => {
      const cur = prev[generationId];
      if (!cur) return prev;
      return { ...prev, [generationId]: { ...cur, custom_tags: savedCommaString } };
    });
  }, []);

  // Handle zoom image
  const handleZoomImage = (imageSrc, imageType, item) => {
    setZoomedImage({ src: imageSrc, type: imageType, item });
    setZoomDialog(true);
  };


  // Handle play media in zoom
  const handlePlayMedia = (mediaSrc, mediaType, item) => {
    setZoomedImage({ src: mediaSrc, type: mediaType, item });
    setZoomDialog(true);
  };

  const isUsageLimitError = (err) => err?.response?.status === 429;

  // Handle download individual files
  const handleDownloadFiles = async () => {
    if (!canDownload) {
      setError('You do not have permission to download items');
      return;
    }
    if (selectedItems.length === 0) {
      setError('Please select at least one item to download');
      return;
    }

    setDownloadingFiles(true);
    try {
      // Get selected item objects
      const itemsToDownload = galleryItems.filter(item => selectedItems.includes(item.id));

      if (FlutterServicesAPI.isFlutterApp()) {
        console.log("📱 Mobile file download detected");

        // Get access token
        const accessToken = Cookies.get('merchant_access_token');
        if (!accessToken) {
          throw new Error('Access token not found. Please login again.');
        }

        // Prepare files array using the POST download endpoint
        const files = itemsToDownload.map(item => {
          const fileType = getFileType(item);
          const downloadUrl = `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/download/${item.id}`;

          return {
            fileName: getGenerationDownloadBaseName(item),
            fileExtension: fileType === 'video' ? 'mp4' : 'jpg',
            downloadUrl,
            generationId: item.id,
            fileType: fileType === 'video' ? 'VIDEO' : 'IMAGE',
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          };
        });

        const downloadResult = await FlutterServicesAPI.downloadFileToStorage({
          accessToken,
          method: 'POST',
          files: files
        });

        if (downloadResult.success) {
          setSuccess(downloadResult.message);
        } else {
          setError(downloadResult.message || 'Download failed');
        }

      } else {
        // Web fallback - download one by one using POST download endpoint
        console.log("🌐 Web file download - downloading individually via /download/{id}");

        let successCount = 0;
        for (const item of itemsToDownload) {
          try {
            const response = await aiGenerationAPI.downloadGeneration(item.id);
            if (!response || !response.data) throw new Error(`Failed to download ${item.id}`);

            const blob = new Blob([response.data]);
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute(
              'download',
              getGenerationDownloadFileName(
                item,
                getFileType(item) === 'video' ? 'mp4' : 'jpg'
              )
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            // Small delay to prevent browser blocking multiple downloads or failing second attempt
            setTimeout(() => {
              window.URL.revokeObjectURL(blobUrl);
            }, 1000);

            successCount++;
            // Small delay to prevent browser blocking multiple downloads
            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (err) {
            if (isUsageLimitError(err)) throw err;
            console.error(`Failed to download item ${item.id}:`, err);
          }
        }

        if (successCount > 0) {
          setSuccess(`Successfully initiated download for ${successCount} files`);
        } else {
          setError('Failed to download selected files');
        }
      }

      setSelectedItems([]);
      setSelectAll(false);

    } catch (error) {
      if (isUsageLimitError(error)) return;
      console.error('Failed to download files:', error);
      setError(error.message || 'Failed to download files');
    } finally {
      setDownloadingFiles(false);
    }
  };

  // Handle download single item (for preview popup)
  const handleDownloadSingleItem = async (item) => {
    if (!canDownload) {
      setError('You do not have permission to download items');
      return;
    }

    setDownloadingFiles(true);
    try {
      if (FlutterServicesAPI.isFlutterApp()) {
        console.log("📱 Mobile file download detected (Single)");

        // Get access token
        const accessToken = Cookies.get('merchant_access_token');
        if (!accessToken) {
          throw new Error('Access token not found. Please login again.');
        }

        const fileType = getFileType(item);
        const downloadUrl = `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/download/${item.id}`;

        const files = [{
          fileName: getGenerationDownloadBaseName(item),
          fileExtension: fileType === 'video' ? 'mp4' : 'jpg',
          downloadUrl: downloadUrl,
          generationId: item.id,
          fileType: fileType === 'video' ? 'VIDEO' : 'IMAGE',
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }];

        const downloadResult = await FlutterServicesAPI.downloadFileToStorage({
          accessToken,
          method: 'POST',
          files: files
        });

        if (downloadResult.success) {
          setSuccess(downloadResult.message);
        } else {
          setError(downloadResult.message || 'Download failed');
        }

      } else {
        // Web fallback - use POST download endpoint
        console.log("🌐 Web file download (Single) via /download/{id}");
        const response = await aiGenerationAPI.downloadGeneration(item.id);

        if (!response || !response.data) throw new Error(`Failed to download ${item.id}`);

        const blob = new Blob([response.data]);
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute(
          'download',
          getGenerationDownloadFileName(
            item,
            getFileType(item) === 'video' ? 'mp4' : 'jpg'
          )
        );
        document.body.appendChild(link);
        link.click();
        // Delay cleanup to ensure mobile browsers complete download
        setTimeout(() => {
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        }, 5000);
        setSuccess('Download started successfully');
      }
    } catch (err) {
      if (isUsageLimitError(err)) return;
      console.error('Download error:', err);
      setError('Failed to download file. Please try again.');
    } finally {
      setDownloadingFiles(false);
    }
  };

  // Handle download as zip
  const handleDownloadZip = async () => {
    if (!canDownload) {
      setError('You do not have permission to download items');
      return;
    }
    if (selectedItems.length === 0) {
      setError('Please select at least one item to download');
      return;
    }

    setDownloadingZip(true);
    try {
      // Single IST timestamp for ZIP basename + filename (mobile + web stay in sync)
      const zipNames = createGalleryZipDownloadNames();

      // Check if running in Flutter app (mobile)
      if (FlutterServicesAPI.isFlutterApp()) {
        console.log("📱 Mobile ZIP download detected");

        // For mobile: Get ZIP URL instead of blob
        const response = await aiGenerationAPI.downloadGalleryAsZipMobile(selectedItems);

        if (response.data.success && response.data.zip_url) {
          let zipUrl = response.data.zip_url;

          // Ensure zipUrl uses the correct BASE_URL for mobile
          if (zipUrl) {
            if (zipUrl.startsWith('http')) {
              // If it's already an absolute URL, replace the origin with BACKEND_BASE
              try {
                const urlObj = new URL(zipUrl);
                zipUrl = `${API_URLS.BACKEND_BASE}${urlObj.pathname}${urlObj.search}`;
              } catch (e) {
                console.error("Error parsing zipUrl:", e);
              }
            } else {
              // If it's a relative path, prefix with BACKEND_BASE
              zipUrl = `${API_URLS.BACKEND_BASE}${zipUrl.startsWith('/') ? '' : '/'}${zipUrl}`;
            }
          }

          // console.log("📱 ZIP URL received:", zipUrl);

          // For mobile apps: Trigger ZIP download to device storage using existing downloadFileToStorage
          try {
            // console.log("📱 Triggering mobile ZIP download to device storage...");
            setSuccess("ZIP file created! Downloading to device storage...");

            // Get access token for authentication
            const accessToken = Cookies.get('merchant_access_token');
            if (!accessToken) {
              throw new Error('Access token not found. Please login again.');
            }

            // Use the existing downloadFileToStorage function for ZIP downloads
            const downloadResult = await FlutterServicesAPI.downloadFileToStorage({
              generationId: zipNames.baseName,
              fileType: 'ZIP',
              // Pass basename only; Flutter appends extension using fileExtension.
              fileName: zipNames.baseName,
              downloadUrl: zipUrl,
              fileExtension: 'zip',
              method: 'GET', // ZIP files are typically served via GET
              headers: {}, // No special headers needed for ZIP downloads
              accessToken: accessToken, // Add access token for authentication
              selectedItems: selectedItems, // Pass selected items for Flutter processing
              id: zipNames.baseName
            });
            if (downloadResult.success) {
              // setSuccess(`ZIP file downloaded to device storage successfully!`);
              console.log("✅ Mobile ZIP download successful:", downloadResult);
            } else {
              console.warn("⚠️ Mobile ZIP download may have failed:", downloadResult);
              // Still show success for ZIP creation, but warn about download
              setSuccess(`ZIP file created successfully! Download initiated. Check your device storage.`);
            }
          } catch (downloadError) {
            console.error("❌ Mobile ZIP download error:", downloadError);
            console.error("❌ Download error details:", {
              error: downloadError,
              message: downloadError.message,
              stack: downloadError.stack,
              zipUrl: zipUrl,
              selectedItems: selectedItems
            });

            // Fallback: show ZIP URL and let user handle manually
            setSuccess(`ZIP file created successfully! URL: ${zipUrl}`);
            // Optionally, you can copy the URL to clipboard or share it
            if (navigator.clipboard) {
              try {
                await navigator.clipboard.writeText(zipUrl);
                setSuccess(`ZIP file created! URL copied to clipboard: ${zipUrl}`);
              } catch (clipboardError) {
                console.warn('Failed to copy to clipboard:', clipboardError);
              }
            }
          }
        } else {
          throw new Error(response.data.message || 'Failed to create ZIP file');
        }
      } else {
        // For web: Continue with existing blob download logic
        console.log("🌐 Web ZIP download");
        const response = await aiGenerationAPI.downloadGalleryAsZip(selectedItems, {
          responseType: 'blob',
        });

        console.log("🌐 ZIP response received:", response);
        console.log("🌐 ZIP response data type:", typeof response.data);
        console.log("🌐 ZIP response data size:", response.data?.size || 'unknown');

        // Validate response data
        if (!response.data || response.data.size === 0) {
          throw new Error('ZIP file is empty or corrupted');
        }

        if (response.data.size < 100) {
          throw new Error(`ZIP file is too small (${response.data.size} bytes). This indicates an error.`);
        }

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', zipNames.fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        setSuccess(`Successfully downloaded ${selectedItems.length} items (ZIP size: ${(response.data.size / 1024).toFixed(1)} KB)`);
      }

      setSelectedItems([]);
      setSelectAll(false);
    } catch (error) {
      if (isUsageLimitError(error)) return;
      console.error('Failed to download zip:', error);
      if (FlutterServicesAPI.isFlutterApp()) {
        setError('Failed to create or download ZIP file. Please try again.');
      } else {
        setError('Failed to download selected items');
      }
    } finally {
      setDownloadingZip(false);
    }
  };

  const getLogoUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;

    // Check if filename contains folder prefix
    if (imagePath.startsWith('uploads/merchant_brand_logos/')) {
      const parts = imagePath.split('/');
      if (parts.length >= 4) {
        const merchant_id = parts[2];
        const filename = parts.slice(3).join('/');
        return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/merchant_brand_logos/${merchant_id}/${filename}`;
      }
    }
    if (imagePath.startsWith('uploads/brand_logos/')) {
      const filename = imagePath.replace('uploads/brand_logos/', '');
      return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/brand_logos/${filename}`;
    }
    if (imagePath.startsWith('uploads/')) {
      return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/${imagePath.replace('uploads/', '')}`;
    }
    return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/${imagePath}`;
  };

  const handlePdfClick = async () => {
    if (!canDownloadPdf) {
      setError('You do not have permission to download items');
      return;
    }
    if (selectedItems.length === 0) {
      setError('Please select at least one item to generate PDF');
      return;
    }

    // Reset inline add brand logo states
    setIsAddingNewLogo(false);
    setNewBrandName('');
    setNewDescription('');
    setNewLogoFile(null);
    setNewLogoError('');

    setLogosLoading(true);
    try {
      const response = await merchantAPI.getBrandLogos();
      const activeLogos = (response?.data?.brand_logos || []).filter(l => l.is_active);
      setBrandLogos(activeLogos);

      const primaryLogo = activeLogos.find(l => l.is_primary);
      if (primaryLogo) {
        setSelectedLogoId(primaryLogo.id);
      } else if (activeLogos.length > 0) {
        setSelectedLogoId(activeLogos[0].id);
      } else {
        setSelectedLogoId(null);
      }
      setLogoDialogOpen(true);
    } catch (err) {
      console.error('Failed to fetch brand logos:', err);
      setBrandLogos([]);
      setSelectedLogoId(null);
      setLogoDialogOpen(true);
    } finally {
      setLogosLoading(false);
    }
  };

  const handleCloseLogoDialog = () => {
    setLogoDialogOpen(false);
    setIsAddingNewLogo(false);
    setNewBrandName('');
    setNewDescription('');
    setNewLogoFile(null);
    setNewLogoError('');
  };

  const handleLogoFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setNewLogoError('');
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    if (!allowedTypes.includes(file.type)) {
      setNewLogoError('Please select only JPG, JPEG, PNG, WebP, or HEIC files.');
      return;
    }
    const maxSize = 6 * 1024 * 1024;
    if (file.size > maxSize) {
      setNewLogoError('File size must be less than 6MB.');
      return;
    }
    setNewLogoFile(file);
  };

  const handleAddNewLogoSubmit = async () => {
    setNewLogoError('');
    if (!newBrandName.trim()) {
      setNewLogoError('Brand name is required');
      return;
    }
    if (!newLogoFile) {
      setNewLogoError('Please select a logo file');
      return;
    }

    setNewLogoUploading(true);
    try {
      if (FlutterServicesAPI.isFlutterApp()) {
        try {
          setIsCheckingPermissions(true);
          const permissionResult = await FlutterServicesAPI.checkFilePermissions();
          if (!permissionResult.can_upload) {
            setNewLogoError('File upload permission denied. Please check your device permissions.');
            return;
          }
        } catch (error) {
          setNewLogoError('Failed to check file permissions. Please try again.');
          return;
        } finally {
          setIsCheckingPermissions(false);
        }
      }

      // 1. Upload Logo image
      const formData = new FormData();
      formData.append('file', newLogoFile);
      formData.append('remove_background', 'true');
      formData.append('enhance_image', 'true');

      const uploadResponse = await merchantAPI.uploadBrandLogo(formData);
      const logoPath = uploadResponse?.data?.file_path;

      if (!logoPath) {
        throw new Error('Failed to retrieve uploaded logo path');
      }

      // 2. Create Brand Logo record
      const brandData = {
        brand_name: newBrandName,
        description: newDescription,
        brand_logo_path: logoPath
      };
      await merchantAPI.createBrandLogo(brandData);

      // 3. Reload logos and select the new logo
      const response = await merchantAPI.getBrandLogos();
      const activeLogos = (response?.data?.brand_logos || []).filter(l => l.is_active);
      setBrandLogos(activeLogos);

      // Select the new logo
      const newLogo = activeLogos.find(l => l.brand_name === newBrandName) || activeLogos[0];
      if (newLogo) {
        setSelectedLogoId(newLogo.id);
      }

      // Reset fields and go back to list
      setNewBrandName('');
      setNewDescription('');
      setNewLogoFile(null);
      setIsAddingNewLogo(false);
      setSuccess('Brand logo uploaded and added successfully');
    } catch (error) {
      console.error('Failed to upload and create brand logo:', error);
      setNewLogoError(error.response?.data?.error || error.message || 'Failed to upload and create brand logo');
    } finally {
      setNewLogoUploading(false);
    }
  };

  const handleDownloadPdf = async (brandLogoId = null) => {
    if (!canDownload) {
      setError('You do not have permission to download items');
      return;
    }
    if (selectedItems.length === 0) {
      setError('Please select at least one item to generate PDF');
      return;
    }

    setDownloadingPdf(true);
    try {
      const result = await FlutterServicesAPI.downloadGalleryPdfCatalogue(selectedItems, brandLogoId);

      if (FlutterServicesAPI.isFlutterApp()) {
        if (result?.success) {
          setSuccess(result.message || 'PDF catalogue download started successfully');
        } else {
          throw new Error(result?.message || 'Failed to trigger PDF download in Flutter app');
        }
      } else {
        if (!result?.data || result.data.size === 0) {
          throw new Error('Received an empty PDF file');
        }

        const blobUrl = window.URL.createObjectURL(new Blob([result.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', createGalleryPdfCatalogueFileName());
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
        setSuccess('PDF catalogue download started successfully');
      }
    } catch (error) {
      if (isUsageLimitError(error)) return;
      console.error('Failed to download PDF catalogue:', error);
      setError(error?.response?.data?.error || error.message || 'Failed to generate PDF catalogue');
    } finally {
      setDownloadingPdf(false);
      setLogoDialogOpen(false);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'warning';
      case 'pending':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get file type
  const getFileType = (item) => {
    if (!item) return 'image';
    // Reels have category 'Reel' but inherit the source's output_format (IMAGE)
    if (item.jewelry_category?.display_name === 'Reel') return 'video';
    return item.output_format?.name === 'VIDEO' ? 'video' : 'image';
  };

  // Get thumbnail URL - use enhanced_image_path for videos/reels if available
  const getThumbnailUrl = (item, size = 'thumbnail') => {
    if (!item) return '';
    const isVideoOrReel = item.output_format?.name === 'VIDEO' || item.jewelry_category?.display_name === 'Reel';
    if (isVideoOrReel && item.enhanced_image_path) {
      return getSecureThumbnailUrl(item.id, size, 'enhanced');
    }
    return getSecureThumbnailUrl(item.id, size);
  };

  // Get preview URL - this should return the actual video/image for preview
  const getPreviewUrl = (item) => {
    if (!item) {
      console.warn('getPreviewUrl called with undefined item');
      return '';
    }

    const isVideoOrReel = item.output_format?.name === 'VIDEO' || item.jewelry_category?.display_name === 'Reel';
    if (isVideoOrReel) {
      return `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/video-stream/${item.id}`;
    } else {
      return getSecureThumbnailUrl(item.id, '1200x1200');
    }
  };

  // Memoized callback functions for video handlers to prevent unnecessary re-renders
  const handleVideoLoad = useCallback((itemId, previewUrl) => {
  }, []);

  const handleVideoError = useCallback((itemId, previewUrl, error) => {
  }, []);

  const handleSelectedVideoLoad = useCallback((itemId, previewUrl) => {
  }, []);

  const handleSelectedVideoError = useCallback((itemId, previewUrl, error) => {
  }, []);

  const handlePreviewVideoLoad = useCallback((itemId, previewUrl) => {
  }, []);

  const handlePreviewVideoError = useCallback((itemId, previewUrl, error) => {
  }, []);

  const handleZoomVideoLoad = useCallback(() => {
  }, []);

  const handleZoomVideoError = useCallback((error) => {
  }, []);

  // Handle download as zip
  return (
    <>
      <DynamicMerchantTabs />

      {/* Catalog pick mode — user came from Catalogs → Add from Gallery */}
      {isCatalogPickMode && (
        <Alert
          severity="info"
          icon={<CollectionsBookmark />}
          sx={{
            mx: { xs: 0, md: 3 },
            mt: 1,
            mb: 1,
            borderRadius: 2,
            bgcolor: '#F3EEFF',
            color: '#1E2939',
            alignItems: { xs: 'flex-start', sm: 'center' },
            '& .MuiAlert-icon': { color: '#651BC6', mt: { xs: '2px', sm: 0 } },
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ fontSize: { xs: '12px', md: '16px' } }}>
              Select images/videos to add to <strong>{addToCatalogCtx.catalogName}</strong>.
              Items already in this catalog are marked in purple.
            </Box>
            <Button
              color="inherit"
              size="small"
              startIcon={<ArrowBack />}
              onClick={handleBackToCatalogs}
              sx={{ fontSize: { xs: '12px', md: '15px' }, textTransform: 'none', fontWeight: 600, color: '#651BC6', whiteSpace: 'nowrap', p: { xs: 0, sm: '4px 8px' }, minWidth: 'auto', '&:hover': { bgcolor: { xs: 'transparent', sm: 'rgba(0, 0, 0, 0.04)' } } }}
            >
              Back to Catalogue
            </Button>
          </Box>
        </Alert>
      )}

      <Box sx={{
        width: '100%',
        px: { xs: 0, sm: 0, md: 0 },
        mx: { xs: 0, sm: 0, md: 0 }
      }}>
        <Paper elevation={3} sx={{
          p: { xs: 0, sm: 3, md: 4 },
          border: { xs: 'none', sm: '1px solid #e5e7eb', md: '1px solid #e5e7eb' },
          boxShadow: 'none',
          backgroundColor: { xs: 'transparent', sm: 'white', md: 'white' },
          borderRadius: '8px'
        }}>
          {/* Header row: Gallery title + Refresh */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ px: { xs: 0, sm: 0 }, pt: { xs: 0, sm: 0 } }}>
            <Typography
              variant="h6"
              fontWeight={700}
              fontSize={{ xs: '18px', sm: '22px', md: '28px' }}
              color="#1E2939"
              fontFamily="var(--font-montserrat-semibold)"
            >
              Gallery
            </Typography>

            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setPage(0);
                setHasMore(true);
                loadGallery({ append: false, targetPage: 0 });
              }}
              disabled={loading}
              sx={{
                borderRadius: 2,
                borderColor: '#e5e7eb',
                color: '#1e293b',
                fontWeight: 600,
                height: '44px',
                minHeight: '44px',
                textTransform: 'none',
                px: { xs: 1.5, md: 2 },
                py: 1,
                fontSize: 15,
                backgroundColor: '#f3f0ff',
                minWidth: { xs: 'auto', md: 'auto' },
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1,
                '&:hover': {
                  backgroundColor: '#f3f0ff',
                  borderColor: '#a78bfa',
                },
                '& .MuiButton-startIcon': {
                  marginRight: 0,
                  marginLeft: 0,
                },
              }}
            >
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>Refresh</Box>
            </Button>
          </Box>

          {/* Search + Filter Toggle UI */}
          <Box sx={{ mb: 2, position: 'relative', zIndex: 115 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Box display="flex" alignItems="center" gap={1} sx={{ flexGrow: 1, maxWidth: { md: '610px' }, width: { xs: '100%', sm: 'auto' } }}>
                {/* Multi-tag search with autocomplete */}
                <Box sx={{ position: 'relative', flex: 1, maxWidth: { md: '550px' } }}>
                  <Box
                    onClick={() => tagInputRef.current && tagInputRef.current.focus()}
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: '6px',
                      minHeight: '44px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '10px',
                      backgroundColor: '#fff',
                      padding: '5px 12px 5px 10px',
                      cursor: 'text',
                      boxSizing: 'border-box',
                      '&:focus-within': { borderColor: '#6318C6', boxShadow: '0 0 0 1px #6318C620' },
                    }}
                  >
                    {/* Search icon */}
                    <Box
                      component="img"
                      src="/assets/img/icons/search-icon.svg"
                      alt="search"
                      sx={{ width: 16, height: 16, flexShrink: 0, opacity: 0.5 }}
                    />
                    {/* Existing tag chips */}
                    {searchTags.map(tag => (
                      <Box
                        key={tag}
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          backgroundColor: '#6318C6', color: '#fff',
                          borderRadius: '20px', px: 1.2, py: 0.3,
                          fontSize: '12px', fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                          fontWeight: 600, flexShrink: 0,
                        }}
                      >
                        {tag}
                        <Box
                          component="span"
                          onClick={(e) => { e.stopPropagation(); removeTagFromSearch(tag); }}
                          sx={{ cursor: 'pointer', lineHeight: 1, fontSize: '14px', ml: 0.3, opacity: 0.8, '&:hover': { opacity: 1 } }}
                        >
                          ×
                        </Box>
                      </Box>
                    ))}
                    {/* Tag text input */}
                    <input
                      ref={tagInputRef}
                      placeholder="Search by attributes"
                      type="text"
                      value={tagInput}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val.endsWith(',')) {
                          const tag = val.slice(0, -1).trim();
                          if (tag) addTagToSearch(tag);
                        } else {
                          setTagInput(val);
                        }
                      }}
                      onFocus={() => { if (tagInput.trim() && tagSuggestions.length > 0) setShowTagDropdown(true); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && tagInput.trim()) {
                          e.preventDefault();
                          addTagToSearch(tagInput.trim());
                        } else if (e.key === 'Backspace' && !tagInput && searchTags.length > 0) {
                          removeTagFromSearch(searchTags[searchTags.length - 1]);
                        }
                      }}
                      style={{
                        border: 'none', outline: 'none', flex: 1, minWidth: '120px',
                        fontSize: '13px', fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                        color: '#1F2937', background: 'transparent', padding: 0,
                      }}
                    />
                    {/* Clear all button */}
                    {searchTags.length > 0 && (
                      <Box
                        component="span"
                        onClick={(e) => { e.stopPropagation(); setSearchTags([]); setSearchTerm(''); setTagInput(''); }}
                        sx={{ cursor: 'pointer', color: '#9ca3af', fontSize: '36px', lineHeight: 1, flexShrink: 0, '&:hover': { color: '#6318C6' } }}
                      >
                        ×
                      </Box>
                    )}
                  </Box>
                  {/* Autocomplete dropdown */}
                  {showTagDropdown && (
                    <Box
                      ref={tagDropdownRef}
                      sx={{
                        position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                        backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 200, overflow: 'hidden',
                        maxHeight: '290px',
                        overflowY: 'auto',
                        padding: '4px 0px 25px 0px',
                      }}
                    >
                      {tagSuggestionsLoading ? (
                        <Box sx={{ px: 2, py: 1.5, fontSize: '13px', color: '#9ca3af', fontFamily: 'var(--font-montserrat-medium)' }}>
                          Searching...
                        </Box>
                      ) : tagSuggestions.length === 0 ? (
                        <Box sx={{ px: 2, py: 1.5, fontSize: '13px', color: '#9ca3af', fontFamily: 'var(--font-montserrat-medium)' }}>
                          No matching tags found
                        </Box>
                      ) : (
                        tagSuggestions.map((suggestion, idx) => (
                          <Box
                            key={idx}
                            onMouseDown={(e) => { e.preventDefault(); addTagToSearch(suggestion); }}
                            sx={{
                              px: 2, py: 1.2, fontSize: '13px', cursor: 'pointer',
                              fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                              color: '#1F2937',
                              '&:hover': { backgroundColor: '#f3f0ff', color: '#6318C6' },
                              borderBottom: idx < tagSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                              display: 'flex', alignItems: 'center', gap: 1,
                            }}
                          >
                            <Box component="span" sx={{ fontSize: '12px', opacity: 0.4 }}>
                              {typeof suggestion === 'string' || suggestion.type === 'Tag' ? <ChevronRightIcon /> : <ChevronRightIcon />}
                            </Box>
                            <Box component="span" sx={{ flexGrow: 1 }}>
                              {typeof suggestion === 'string' ? suggestion : suggestion.label}
                            </Box>
                            {suggestion.type && suggestion.type !== 'Tag' && (
                              <Box component="span" sx={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', ml: 'auto' }}>
                                {suggestion.type}
                              </Box>
                            )}
                          </Box>
                        ))
                      )}
                    </Box>
                  )}
                </Box>
                {/* Filter toggle button and Dropdown Wrapper */}
                <Box ref={filterPanelRef} sx={{ position: { xs: 'static', md: 'relative' } }}>
                  <Box
                    onClick={() => {
                      const isOpening = !filterPanelOpen;
                      setFilterPanelOpen(isOpening);
                      if (isOpening) {
                        syncTempFilters();
                      }
                    }}
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '10px',
                      bgcolor: '#A78BFA',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                      '&:hover': { bgcolor: '#9168f0' },
                      transition: 'background 0.2s',
                    }}
                  >
                    <Box
                      component="img"
                      src="/assets/img/icons/filter-icon.svg"
                      alt="filter"
                      sx={{ width: 20, height: 20 }}
                    />
                  </Box>

                  {/* Filter dropdown panel */}
                  {filterPanelOpen && (
                    <Box
                      sx={{
                        mt: { xs: 1, md: 0 },
                        p: 2.5,
                        bgcolor: '#fff',
                        borderRadius: '16px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        border: '1px solid #f0f0f0',
                        zIndex: 20,
                        position: 'absolute',
                        bottom: { xs: 'auto', md: 'auto' },
                        top: { xs: '100%', md: 0 },
                        left: { xs: 0, md: '100%' },
                        right: { xs: 0, md: 'auto' },
                        ml: { xs: 0, md: 2 },
                        width: { xs: 'auto', md: 'max-content' },
                        minWidth: { md: '450px' },
                        maxWidth: { md: '500px' }
                      }}
                    >
                      {/* Header: Filters | Apply  Clear All */}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 16, color: '#1E2939' }}>
                          Filters
                        </Typography>
                        <Box display="flex" gap={2} alignItems="center">
                          <Typography
                            onClick={applyFilters}
                            sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 14, color: '#111827', cursor: 'pointer' }}
                          >
                            Apply
                          </Typography>
                          <Typography
                            onClick={() => {
                              setStatusFilter('');
                              setStartDate('');
                              setEndDate('');
                              setSearchTerm('');
                              setDateError('');
                              setTempStatusFilter('');
                              setTempStartDate('');
                              setTempEndDate('');
                              setTempSearchTerm('');
                              setSearchTags([]);
                              setTagInput('');
                            }}
                            sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 14, color: '#7C3AED', cursor: 'pointer' }}
                          >
                            Clear All
                          </Typography>
                        </Box>
                      </Box>

                      {/* Date Range section */}
                      <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 14, color: '#364153', mb: 2 }}>
                        Date Range
                      </Typography>
                      {dateError && (
                        <Typography sx={{ color: '#ef4444', fontSize: 12, mb: 1 }}>{dateError}</Typography>
                      )}
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {/* From date */}
                          <Box sx={{ flex: 1, minWidth: 130, position: 'relative' }}>
                            <Box
                              component="label"
                              sx={{
                                position: 'absolute',
                                top: '-4px',
                                left: '16px',
                                fontSize: '11px',
                                color: '#6B7280',
                                bgcolor: '#fff',
                                px: 0.5,
                                fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                                zIndex: 2,
                                lineHeight: 1,
                              }}
                            >
                              From
                            </Box>
                            <DatePicker
                              format="DD/MM/YYYY"
                              value={tempStartDate ? dayjs(tempStartDate) : null}
                              maxDate={dayjs().endOf('day')}
                              onChange={(newValue) => {
                                const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                                setTempStartDate(dateStr);
                                const err = validateDates(dateStr, tempEndDate);
                                setDateError(err);
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  InputProps: {
                                    sx: {
                                      height: '40px',
                                      borderRadius: '100px',
                                      fontSize: 12,
                                      fontFamily: 'var(--font-montserrat-medium)',
                                      color: '#1F2937',
                                      backgroundColor: '#fff',
                                      '& fieldset': {
                                        border: '1.5px solid #D1D5DB',
                                      },
                                      '&:hover fieldset': {
                                        border: '1.5px solid #9CA3AF',
                                      },
                                      '&.Mui-focused fieldset': {
                                        border: '1.5px solid #1E293B',
                                      },
                                      '& .MuiInputBase-input': {
                                        padding: '0 14px',
                                        height: '40px',
                                        boxSizing: 'border-box'
                                      }
                                    }
                                  }
                                }
                              }}
                            />
                          </Box>
                          {/* To date */}
                          <Box sx={{ flex: 1, minWidth: 130, position: 'relative' }}>
                            <Box
                              component="label"
                              sx={{
                                position: 'absolute',
                                top: '-4px',
                                left: '16px',
                                fontSize: '11px',
                                color: '#6B7280',
                                bgcolor: '#fff',
                                px: 0.5,
                                fontFamily: 'var(--font-montserrat-medium)',
                                zIndex: 2,
                                lineHeight: 1,
                              }}
                            >
                              To
                            </Box>
                            <DatePicker
                              format="DD/MM/YYYY"
                              value={tempEndDate ? dayjs(tempEndDate) : null}
                              maxDate={dayjs().endOf('day')}
                              onChange={(newValue) => {
                                const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                                setTempEndDate(dateStr);
                                const err = validateDates(tempStartDate, dateStr);
                                setDateError(err);
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  InputProps: {
                                    sx: {
                                      height: '40px',
                                      borderRadius: '100px',
                                      fontSize: 12,
                                      fontFamily: 'var(--font-montserrat-medium)',
                                      color: '#1F2937',
                                      backgroundColor: '#fff',
                                      '& fieldset': {
                                        border: '1.5px solid #D1D5DB',
                                      },
                                      '&:hover fieldset': {
                                        border: '1.5px solid #9CA3AF',
                                      },
                                      '&.Mui-focused fieldset': {
                                        border: '1.5px solid #1E293B',
                                      },
                                      '& .MuiInputBase-input': {
                                        padding: '0 14px',
                                        height: '40px',
                                        boxSizing: 'border-box'
                                      }
                                    }
                                  }
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      </LocalizationProvider>
                    </Box>
                  )}
                </Box>
              </Box>

            </Box>
          </Box>


          {/* New Selection Controls will be added here */}


          {loading && <LinearProgress sx={{ mb: 2 }} />}


          {/* Select All Items - always visible */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} sx={{ mt: 0, px: { xs: 0, sm: 0 } }}>
            {/* Left: Select/Deselect All label acting as button */}
            <Typography
              component="span"
              onClick={handleSelectAll.bind(null, { target: { checked: !selectAll && selectedItems.length === 0 ? true : selectedItems.length > 0 ? false : true } })}
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: '#6318C6',
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {selectedItems.length > 0 ? 'Deselect All Items' : 'Select All Items'}
            </Typography>

            {/* Right: count */}
            {selectedItems.length > 0 && (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  fontSize: { xs: 14, sm: 16 },
                }}
              >
                {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} Selected
              </Typography>
            )}
          </Box>

          {/* Gallery Grid */}
          <Grid container spacing={2} sx={{ px: { xs: 0, sm: 0 } }}>

            {/* Placeholder grid on first load so the area is not blank while API + thumbnails load */}
            {loading && galleryItems.length === 0 &&
              Array.from({ length: rowsPerPage }).map((_, idx) => (
                <Grid item xs={6} sm={4} md={4} lg={2.4} key={`gallery-initial-skel-${idx}`}>
                  <Box
                    sx={{
                      border: '2px solid #e5e7eb',
                      borderRadius: '14.3px',
                      overflow: 'hidden',
                      width: '100%',
                      p: '4px',
                      bgcolor: '#fff',
                      boxShadow: '0px 1.06px 5.3px 0px rgba(0,0,0,0.07)',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: { xs: 'auto', md: '390px' },
                        aspectRatio: { xs: '2/3', md: 'auto' },
                        borderRadius: '10px',
                      }}
                    >
                      <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" sx={{ borderRadius: '10px' }} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, px: 0.5 }}>
                      <Skeleton variant="circular" width={36} height={36} />
                      <Skeleton variant="circular" width={36} height={36} />
                    </Box>
                  </Box>
                </Grid>
              ))}

            {/* Gallery Items */}
            {galleryItems.map((item) => {
              const fileType = getFileType(item);
              const previewUrl = getThumbnailUrl(item);
              const alreadyInCatalog = isCatalogPickMode && existingInCatalogSet.has(item.id);
              const isSelected = !alreadyInCatalog && selectedItems.includes(item.id);
              const isReel = item.jewelry_category?.display_name === 'Reel';


              return (
                <Grid item xs={6} sm={4} md={4} lg={2.4} key={item.id}>
                  {/* Card wrapper with white box shadow as per design */}
                  <Box sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                    backgroundColor: '#fff',
                    borderRadius: '14.3px',
                    boxShadow: alreadyInCatalog
                      ? '0 4px 16px rgba(99, 24, 198, 0.2)'
                      : isSelected
                        ? '0 4px 16px rgba(99, 24, 198, 0.2)'
                        : '0px 1.06px 5.3px 0px rgba(0,0,0,0.07)',
                    border: alreadyInCatalog
                      ? '2px solid #6318C6'
                      : isSelected
                        ? '2px solid #6318C6'
                        : '2px solid transparent',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden', // Kept hidden as there are no dropdowns here, but contentVisibility is removed for consistency.
                    p: '4px',
                  }}>

                    {/* REEL label — UI overlay (never cropped by object-fit cover) */}
                    {isReel && (
                      <Box sx={{
                        position: 'absolute', top: 8, left: 8, zIndex: 5, pointerEvents: 'none',
                        display: 'flex', alignItems: 'center', gap: '3px', px: '7px', py: '3px',
                        background: 'linear-gradient(90deg, #A864ED 0%, #7B3FE4 100%)',
                        borderRadius: '4px 12px 12px 4px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.30)',
                      }}>
                        <PlayArrow sx={{ color: '#fff', fontSize: 13 }} />
                        <Typography sx={{ color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: 1, letterSpacing: '0.5px', fontFamily: 'var(--font-montserrat-semibold)' }}>
                          REEL
                        </Typography>
                      </Box>
                    )}

                    {alreadyInCatalog ? (
                      <Box sx={{ position: 'relative', borderRadius: '6px', overflow: 'hidden', opacity: 0.92 }}>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 4,
                            width: { xs: 21, md: 26 },
                            height: { xs: 21, md: 26 },
                            borderRadius: '4px',
                            border: '2px solid #6318C6',
                            bgcolor: '#6318C6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          }}
                        >
                          <svg
                            width="10"
                            height="8"
                            viewBox="0 0 14 11"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M1 5L5 9L13 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </Box>
                        {fileType === 'video' ? (
                          <Box sx={{ width: '100%', height: { xs: 'auto', md: '390px' }, aspectRatio: { xs: '2/3', md: 'auto' } }}>
                            <SecureImage
                              src={previewUrl}
                              alt=""
                              useThumbnail
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px' }}
                              {...GALLERY_THUMB_IMG_HINTS}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ width: '100%', height: { xs: 'auto', md: '390px' }, aspectRatio: { xs: '2/3', md: 'auto' } }}>
                            <SecureImage
                              src={previewUrl}
                              alt=""
                              useThumbnail
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px' }}
                              {...GALLERY_THUMB_IMG_HINTS}
                            />
                          </Box>
                        )}
                      </Box>
                    ) : !isSelected ? (
                      <Box
                        onClick={() => {
                          if (isCatalogPickMode) {
                            handleItemSelect(item.id);
                          } else if (canPreview) {
                            handlePreview(item);
                          }
                        }}
                        sx={{
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          border: 'none',
                          borderRadius: '6px',
                          overflow: 'hidden',
                          '&:hover': {
                            transform: 'scale(1.01)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          },
                        }}
                      >
                        {/* Checkbox - Top Right (custom square, unchecked) */}
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            handleItemSelect(item.id);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 4,
                            cursor: 'pointer',
                            width: { xs: 21, md: 26 },
                            height: { xs: 21, md: 26 },
                            borderRadius: '4px',
                            border: '3px solid rgba(255,255,255,0.85)',
                            // backgroundColor: 'rgba(255,255,255,0.35)',
                            // backdropFilter: 'blur(2px)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                          }}
                        />

                        {/* Media */}
                        {fileType === 'video' ? (
                          <Box sx={{ position: 'relative', width: '100%', height: { xs: 'auto', md: '390px' }, aspectRatio: { xs: '2/3', md: 'auto' } }}>
                            <SecureImage
                              src={previewUrl}
                              alt={`Video thumbnail for ${item.id}`}
                              useThumbnail={true}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px' }}
                              {...GALLERY_THUMB_IMG_HINTS}
                            />
                            {canPreview && (
                              <Box sx={{ position: 'absolute', border: '2px solid #6400CD', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'rgba(255,255,255,0.6)', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3, pointerEvents: 'none' }}>
                                <PlayArrow sx={{ color: '#6400CD', fontSize: 24 }} />
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ position: 'relative', width: '100%', height: { xs: 'auto', md: '390px' }, aspectRatio: { xs: '2/3', md: 'auto' } }}>
                            <SecureImage
                              src={previewUrl}
                              alt={`Generated ${fileType}`}
                              useThumbnail={true}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px' }}
                              onLoad={() => { }}
                              onError={() => { }}
                              {...GALLERY_THUMB_IMG_HINTS}
                            />
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box
                        onClick={() => handleItemSelect(item.id)}
                        sx={{
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          border: 'none',
                          borderRadius: '10px',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          onClick={(e) => { e.stopPropagation(); handleItemSelect(item.id); }}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 4,
                            cursor: 'pointer',
                            width: { xs: 21, md: 26 },
                            height: { xs: 21, md: 26 },
                            borderRadius: '4px',
                            border: `2px solid #6318C6`,
                            backgroundColor: selectedItems.includes(item.id) ? '#6318C6' : '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {/* Checkmark SVG - sized proportionally */}
                          <svg
                            width="10"
                            height="8"
                            viewBox="0 0 14 11"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M1 5L5 9L13 1" stroke={selectedItems.includes(item.id) ? '#fff' : '#6318C6'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </Box>


                        {/* Media */}
                        {fileType === 'video' ? (
                          <Box sx={{ position: 'relative', width: '100%', height: { xs: 'auto', md: '390px' }, aspectRatio: { xs: '2/3', md: 'auto' } }}>
                            <SecureImage
                              src={previewUrl}
                              alt={`Video thumbnail for ${item.id}`}
                              useThumbnail={true}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px' }}
                              {...GALLERY_THUMB_IMG_HINTS}
                            />
                            {canPreview && (
                              <Box sx={{ position: 'absolute', border: '2px solid #6400CD', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'rgba(255,255,255,0.6)', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3, pointerEvents: 'none' }}>
                                <PlayArrow sx={{ color: '#6400CD', fontSize: 24 }} />
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ position: 'relative', width: '100%', height: { xs: 'auto', md: '390px' }, aspectRatio: { xs: '2/3', md: 'auto' } }}>
                            <SecureImage
                              src={previewUrl}
                              alt={`Generated ${fileType}`}
                              useThumbnail={true}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
                              {...GALLERY_THUMB_IMG_HINTS}
                            />
                          </Box>
                        )}

                        {/* Selection Overlay */}
                        {selectedItems.includes(item.id) && (
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, rgba(168, 100, 237, 0.2) 0%, rgba(245, 183, 96, 0.2) 100%)',
                            zIndex: 1,
                            pointerEvents: 'none'
                          }} />
                        )}
                      </Box>
                    )}

                    {/* Bottom action bar — always rendered below image */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 0.5,
                        py: 0.75,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Left: Zoom icon */}
                      {canPreview && (
                        <Tooltip title="Zoom / Preview">
                          <Box
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreview(item);
                            }}
                            sx={cardIconBtnStyle}
                          >
                            <img src="/assets/img/icons/zoom1.svg" alt="zoom" style={{ width: 17, height: 17 }} />
                          </Box>
                        </Tooltip>
                      )}

                      {/* Right: Info + Share icons */}
                      <Box display="flex" alignItems="center" gap={0.75} sx={{ ml: 'auto' }}>
                        {/* Info icon */}
                        <Tooltip title="Info">
                          <Box
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              setInfoItem(item);
                              setInfoDialog(true);
                            }}
                            sx={cardIconBtnStyle}
                          >
                            <img src="/assets/img/icons/info1.svg" alt="info" style={{ width: 20, height: 20 }} />
                          </Box>
                        </Tooltip>

                        {/* Share icon (triggers ShareSocialButton hidden click) */}
                        {canShare && (
                          <Tooltip title="Share">
                            <Box
                              component="span"
                              sx={{ ...cardIconBtnStyle, position: 'relative', overflow: 'hidden' }}
                            >
                              {/* Invisible ShareSocialButton overlay to keep share functionality */}
                              <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, zIndex: 2, '& > *': { width: '100%', height: '100%' } }}>
                                <ShareSocialButton
                                  imageUrl={getPreviewUrl(item)}
                                  isVideo={getFileType(item) === 'video'}
                                  shareTitle="Amazing AI-generated artwork! 🎨✨"
                                  shareText={getFileType(item) === 'video'
                                    ? `✨ Amazing AI-generated video created with Glammify! 🎥\n\nTransform your ideas into stunning visual content with our AI-powered platform. Perfect for social media, marketing, and creative projects! 🚀\n\nTry Glammify: ${getBaseUrl()}\n\n#Glammify #AIGenerated #Creative #Video #AI #Innovation #DigitalArt #ContentCreation`
                                    : `✨ Stunning AI-generated image created with Glammify! 🎨\n\nBring your creative vision to life with our advanced AI technology. Perfect for social media, marketing, and artistic projects! 🚀\n\nTry Glammify: ${getBaseUrl()}\n\n#Glammify #AIGenerated #Creative #Art #AI #Innovation #DigitalArt #ContentCreation`}
                                  size="small"
                                  orientation="horizontal"
                                  multiShare={true}
                                  selectedItems={[item.id]}
                                  selectedItemsData={[item]}
                                  getPreviewUrl={getPreviewUrl}
                                  getFileType={getFileType}
                                  category={item.jewelry_category}
                                  material={item.jewelry_material}
                                  bustFinish={item.bust_finish}
                                  otherAttributes={[
                                    item.model_type,
                                    item.background_type,
                                    item.bust_color,
                                    item.gender_option,
                                    item.skin_tone,
                                    item.ethnicity,
                                    item.clothing_type,
                                    item.bust_type
                                  ].filter(attr => attr && attr.display_name)}
                                  onShareStatus={(status) => {
                                    if (status?.type === 'success') {
                                      setSuccess(typeof status.message === 'string' ? status.message : JSON.stringify(status.message));
                                    } else if (status?.type === 'error') {
                                      setError(typeof status.message === 'string' ? status.message : JSON.stringify(status.message));
                                    }
                                  }}
                                />
                              </Box>
                              {/* Visible share icon */}
                              <img src="/assets/img/icons/share1.svg" alt="share" style={{ width: 20, height: 20, position: 'relative', zIndex: 1, pointerEvents: 'none' }} />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                  </Box>{/* end card wrapper */}
                </Grid>
              );
            })}
          </Grid>

          {/* Next page skeletons */}
          {showNextSkeleton && (
            <Grid container spacing={2} sx={{ px: { xs: 0, sm: 0 }, mt: 0 }}>
              {Array.from({ length: rowsPerPage }).map((_, idx) => (
                <Grid item xs={6} sm={6} md={4} lg={2.4} key={`skeleton-${idx}`}>
                  <Box sx={{ border: '2px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', width: '100%', aspectRatio: '1 / 1' }}>
                    <Skeleton
                      variant="rectangular"
                      height="100%"
                      animation="wave"
                      sx={{
                        borderRadius: '10px',
                        '&::after': {
                          background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)',
                          animationDuration: '0.9s',
                          animationTimingFunction: 'linear'
                        }
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Infinite scroll sentinel */}
          <Box ref={sentinelRef} sx={{ height: 1 }} />
          {isFetchingMore && (
            <Box display="flex" justifyContent="center" mt={2}>
              <LinearProgress sx={{ width: '50%' }} />
            </Box>
          )}

          {/* No items message */}
          {!loading && galleryItems.length === 0 && (
            <Box textAlign="center" py={4}>
              <PhotoLibrary sx={{ fontSize: 64, color: '#9ca3af', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" mb={1}>
                No items found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {typeFilter
                  ? 'Try adjusting your type filter'
                  : 'Start generating images and videos to see them here'
                }
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
      {/* Enhanced Zoom Dialog */}
      <Dialog
        open={zoomDialog}
        onClose={() => setZoomDialog(false)}
        maxWidth={false}
        fullWidth={false}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 1, sm: 2, md: 0 },
            padding: { xs: 0.5, sm: 1, md: 20 },
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: 'none',
            maxHeight: 'none',
            width: 'auto',
            height: 'auto',
            overflow: 'visible'
          }
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          {zoomedImage && (
            <>
              {zoomedImage.type === 'video' ? (
                <Box sx={{ position: 'relative' }}>
                  <SecureVideo
                    src={zoomedImage.src}
                    poster={getThumbnailUrl(zoomedImage.item, '1200x1200')}
                    controls={true} // Show video controls in popup
                    autoPlay={true}
                    muted={true}
                    style={{
                      maxWidth: '85vw',
                      maxHeight: '85vh',
                      objectFit: 'contain',
                      borderRadius: '10px',
                      backgroundColor: '#000'
                    }}
                    onLoad={(e) => handleZoomVideoLoad(e)}
                    onError={(e) => handleZoomVideoError(e)}
                  />
                </Box>
              ) : (
                <SecureImage
                  src={zoomedImage.src}
                  alt={`Zoomed ${zoomedImage.type.toLowerCase()}`}
                  useThumbnail={true}
                  thumbnailSize="1200x1200"
                  style={{
                    maxWidth: '85vw',
                    maxHeight: '85vh',
                    objectFit: 'contain',
                    borderRadius: '10px',
                    backgroundColor: '#000'
                  }}
                />
              )}

              {/* Actions below image/video */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 2,
                  px: 1
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Left: Share Button */}
                {zoomedImage.item && canShare && (
                  <ShareSocialButton
                    imageUrl={getPreviewUrl(zoomedImage.item)}
                    isVideo={zoomedImage.type === 'video'}
                    shareTitle="Amazing AI-generated artwork! 🎨✨"
                    shareText={zoomedImage.type === 'video'
                      ? `✨ Amazing AI-generated video created with Glammify! 🎥\n\nTransform your ideas into stunning visual content with our AI-powered platform. Perfect for social media, marketing, and creative projects! 🚀\n\nTry Glammify: ${getBaseUrl()}\n\n#Glammify #AIGenerated #Creative #Video #AI #Innovation #DigitalArt #ContentCreation`
                      : `✨ Stunning AI-generated image created with Glammify! 🎨\n\nBring your creative vision to life with our advanced AI technology. Perfect for social media, marketing, and artistic projects! 🚀\n\nTry Glammify: ${getBaseUrl()}\n\n#Glammify #AIGenerated #Creative #Art #AI #Innovation #DigitalArt #ContentCreation`}
                    size="medium"
                    orientation="horizontal"
                    multiShare={true}
                    selectedItems={[zoomedImage.item.id]}
                    selectedItemsData={[zoomedImage.item]}
                    getPreviewUrl={getPreviewUrl}
                    getFileType={getFileType}
                    category={zoomedImage.item.jewelry_category}
                    material={zoomedImage.item.jewelry_material}
                    bustFinish={zoomedImage.item.bust_finish}
                    otherAttributes={[
                      zoomedImage.item.model_type,
                      zoomedImage.item.background_type,
                      zoomedImage.item.bust_color,
                      zoomedImage.item.gender_option,
                      zoomedImage.item.skin_tone,
                      zoomedImage.item.ethnicity,
                      zoomedImage.item.clothing_type,
                      zoomedImage.item.bust_type
                    ].filter(attr => attr && attr.display_name)}
                    sx={{
                      '& .MuiIconButton-root': {
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        color: 'black',
                        backdropFilter: 'blur(8px)',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 1)',
                        }
                      }
                    }}
                    onShareStatus={(status) => {
                      if (status?.type === 'success') {
                        setSuccess(typeof status.message === 'string' ? status.message : JSON.stringify(status.message));
                      } else if (status?.type === 'error') {
                        setError(typeof status.message === 'string' ? status.message : JSON.stringify(status.message));
                      }
                    }}
                  />
                )}

                {/* Right: Download Button */}
                {zoomedImage.item && canDownload && (
                  <Tooltip title="Download">
                    {/* span wrapper required by MUI: iOS WebKit blocks all events on disabled
                        elements so Tooltip never receives mouseenter/touchstart without it. */}
                    <span>
                      <IconButton
                        onClick={() => handleDownloadSingleItem(zoomedImage.item)}
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          color: 'black',
                          backdropFilter: 'blur(8px)',
                          width: '40px',
                          height: '40px',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 1)',
                          }
                        }}
                      >
                        <GetApp sx={{ fontSize: 24 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
              {/* Close Button - Positioned outside the image/video */}
              <IconButton
                onClick={() => setZoomDialog(false)}
                sx={{
                  position: 'absolute',
                  top: -25,
                  right: -25,
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.8)',
                  },
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  zIndex: 10,
                }}
              >
                <Cancel sx={{ fontSize: 25 }} />
              </IconButton>
            </>
          )}
        </Box>
      </Dialog>

      {/* Detailed generation preview dialog */}
      <GenerationPreviewDialog
        open={previewDialog}
        onClose={() => setPreviewDialog(false)}
        generation={selectedItem}
      />

      <GenerationInfoPopup
        open={infoDialog}
        onClose={() => setInfoDialog(false)}
        item={infoItem}
        PRIMARY_GRADIENT={PRIMARY_GRADIENT}
        onRegenerate={handleRegenerateFromInfo}
        onCustomTagsSaved={handleCustomTagsSaved}
      />

      <AddToCatalogDialog
        open={addToCatalogDialogOpen}
        onClose={() => setAddToCatalogDialogOpen(false)}
        selectedCount={selectedItems.filter(id => !existingInCatalogSet.has(id)).length}
        onAdd={handleDialogAdd}
      />

      <ReelCreationDialog
        open={reelDialogOpen}
        onClose={() => setReelDialogOpen(false)}
        onReelCreated={prependNewGalleryItems}
        selectedVideoIds={reelVideoIds}
      />

      {/* Too-many-videos popup for reels (max 4) */}
      <Dialog
        open={reelLimitOpen}
        onClose={() => setReelLimitOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <Box sx={{ background: PRIMARY_GRADIENT, px: 3, py: 2 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 17, fontFamily: 'var(--font-montserrat-semibold)' }}>
            Too many videos selected
          </Typography>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Typography sx={{ fontSize: 14, color: '#374151', fontFamily: 'var(--font-montserrat-medium)' }}>
            A reel can include a maximum of <b>{REEL_MAX_VIDEOS} images/videos</b>. You currently have{' '}
            <b>{reelSelectedCount}</b> images/videos selected. Please deselect{' '}
            <b>{Math.max(reelSelectedCount - REEL_MAX_VIDEOS, 0)}</b> and try again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            onClick={() => setReelLimitOpen(false)}
            sx={{
              borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: 14,
              background: PRIMARY_GRADIENT, color: '#fff', px: 3,
              '&:hover': { opacity: 0.92, background: PRIMARY_GRADIENT },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auto-close alerts */}
      <AutoCloseAlert
        show={!!error}
        message={error}
        severity="error"
        onClose={() => setError('')}
        autoCloseDelay={6000}
      />
      <AutoCloseAlert
        show={!!success}
        message={success}
        severity="success"
        onClose={() => setSuccess('')}
        autoCloseDelay={4000}
      />



      {selectedItems.length > 0 && isCatalogPickMode && (
        <Box
          position="fixed"
          bottom={{ xs: '65px', md: '30px' }}
          left="50%"
          sx={{
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: { xs: '100%', md: '420px' },
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            px: { xs: 2, md: 0 },
          }}
        >
          <Button
            variant="contained"
            fullWidth
            onClick={() => handleDialogAdd(addToCatalogCtx.catalogId)}
            disabled={addingToCatalog}
            sx={{
              background: PRIMARY_GRADIENT,
              borderRadius: { xs: '20px 20px 0 0', md: '50px' },
              color: 'white',
              fontWeight: 700,
              fontSize: '15px',
              height: '50px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              textTransform: 'none',
              fontFamily: 'var(--font-montserrat-semibold)',
              '&:hover': { background: PRIMARY_GRADIENT, opacity: 0.92 },
            }}
          >
            {addingToCatalog
              ? 'Adding…'
              : `Add ${selectedItems.filter(id => !existingInCatalogSet.has(id)).length} item${selectedItems.filter(id => !existingInCatalogSet.has(id)).length !== 1 ? 's' : ''} to Catalog`}
          </Button>
        </Box>
      )}

      {selectedItems.length > 0 && !isCatalogPickMode && (
        <Box
          display="flex"
          position="fixed"
          bottom={{ xs: '65px', md: '30px' }} // Touching the footer nav
          left="50%"
          sx={{
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: { xs: '100%', md: '400px' },
            zIndex: 1100,
            background: PRIMARY_GRADIENT,
            borderRadius: { xs: '20px 20px 0 0', md: '50px' },
            color: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 1, md: 2 },
            height: '50px',
            gap: { xs: 1, md: 2 },
            transition: 'all 0.3s ease',
          }}
        >
          {/* Mobile Download */}
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            gap={0.2}
            onClick={!downloadingFiles && !downloadingZip && !downloadingPdf ? handleDownloadFiles : undefined}
            sx={{ cursor: 'pointer', opacity: (downloadingFiles || downloadingZip || downloadingPdf) ? 0.6 : 1, width: 'auto', px: 1 }}
          >
            <img src={DOWNLOAD_SELECTED_ICON} alt="" style={{ width: 22 }} />
            <Typography variant="body2" sx={{ fontSize: '14.44px', fontWeight: 500, fontFamily: 'var(--font-montserrat-medium) !important', color: 'white' }}>Download</Typography>
          </Box>

          {canShare && <Box sx={{ width: '1px', height: '20px', bgcolor: 'rgba(255,255,255,0.6)' }} />}

          {/* Mobile Share */}
          {canShare && <Box sx={{ display: 'flex', justifyContent: 'center', width: 'auto', px: 1 }}>
            {(() => {
              const firstSelectedItem = galleryItems.find(item => item.id === selectedItems[0]) || selectedItemsCache[selectedItems[0]];
              const displayItem = firstSelectedItem || galleryItems[0];
              if (!displayItem) return null;
              return (
                <ShareSocialButton
                  variant="icon"
                  disabled={selectedItems.length === 0}
                  imageUrl={getPreviewUrl(displayItem)}
                  isVideo={getFileType(displayItem) === 'video'}
                  selectedItems={selectedItems}
                  multiShare={true}
                  selectedItemsData={selectedItems.map(id => selectedItemsCache[id] || galleryItems.find(item => item.id === id)).filter(Boolean)}
                  getPreviewUrl={getPreviewUrl}
                  getFileType={getFileType}
                  category={displayItem?.jewelry_category}
                  material={displayItem?.jewelry_material}
                  bustFinish={displayItem?.bust_finish}
                  otherAttributes={[
                    displayItem?.model_type,
                    displayItem?.background_type,
                    displayItem?.bust_color,
                    displayItem?.gender_option,
                    displayItem?.skin_tone,
                    displayItem?.ethnicity,
                    displayItem?.clothing_type,
                    displayItem?.bust_type
                  ].filter(attr => attr && attr.display_name)}
                  sx={{
                    bgcolor: 'transparent',
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.2,
                    width: '100%',
                    padding: 0,
                    textTransform: 'none',
                    minWidth: 'auto',
                    boxShadow: 'none',
                    border: 'none',
                    '&:hover': { bgcolor: 'transparent', boxShadow: 'none' },
                    '&.Mui-disabled': { color: 'white', opacity: 1 } // Keep text white even when selection is 0
                  }}
                  onShareStatus={(status) => {
                    if (status?.type === 'success') {
                      setSuccess(status.message);
                    } else if (status?.type === 'error') {
                      setError(status.message);
                    }
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                  >
                    <img src={SHARE_SELECTED_ICON} alt="" style={{ width: 22 }} />
                    <Typography variant="body2" sx={{ fontSize: '14.44px', fontWeight: 500, fontFamily: 'var(--font-montserrat-medium) !important', color: 'white' }}>Share</Typography>
                  </Box>
                </ShareSocialButton>
              );
            })()}
          </Box>}

          <Box sx={{ width: '1px', height: '20px', bgcolor: 'rgba(255,255,255,0.6)' }} />

          {/* Mobile More */}
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            gap={0.2}
            onClick={handleMoreClick}
            sx={{ cursor: 'pointer', width: 'auto', px: 1 }}
          >
            <img src={MORE_ICON} alt="" style={{ width: 22 }} />
            <Typography variant="body2" sx={{ fontSize: '14.44px', fontWeight: 500, fontFamily: 'var(--font-montserrat-medium) !important', color: 'white' }}>More</Typography>
          </Box>
        </Box>
      )}

      {/* More Menu */}
      <Menu
        anchorEl={moreAnchorEl}
        open={openMore}
        onClose={handleMoreClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mb: 1,
            minWidth: '175px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            mb: { xs: 0, md: '20px' }
          }
        }}
      >
        {moreOptions.map((option) => (
          <MenuItem
            key={option.label}
            onClick={option.disabled ? undefined : option.onClick}
            disabled={option.disabled}
            sx={{
              py: { xs: 0.5, md: 1 },   // mobile: 0.5 → desktop: 1
              px: { xs: 2, md: 2 },     // mobile: 2 → desktop: 3
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minHeight: '36px',
              '&:hover': { bgcolor: option.disabled ? 'transparent' : '#F9F6FE' },
              '&.Mui-disabled': { opacity: 0.7, color: '#9CA3AF' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
              <img src={option.icon} alt="" style={{ width: 22, opacity: option.disabled ? 0.5 : 1 }} />
              <Typography sx={{
                fontWeight: 500,
                fontSize: '13.02px',
                color: option.disabled ? '#9CA3AF' : '#1F2937',
                fontFamily: 'var(--font-montserrat-medium) !important'
              }}>
                {option.label}
              </Typography>
              {option.showComingSoon && (
                <Box sx={{
                  ml: 1,
                  background: PRIMARY_GRADIENT,
                  color: 'white',
                  px: 0.8,
                  py: 0.2,
                  borderRadius: '100px',
                  fontSize: '9px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
                }}>
                  Coming Soon
                </Box>
              )}
              {option.showNew && (
                <Box sx={{
                  ml: 1,
                  background: PRIMARY_GRADIENT,
                  color: 'white',
                  px: 0.8,
                  py: 0.2,
                  borderRadius: '100px',
                  fontSize: '9px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
                }}>
                  NEW
                </Box>
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Dynamic Brand Logo Selection Popup for Gallery PDF Catalogs */}
      <Dialog
        open={logoDialogOpen}
        onClose={handleCloseLogoDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            padding: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.18)'
          }
        }}
      >
        <DialogTitle sx={{ p: 1, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, color: '#1E2939' }}>
            {isAddingNewLogo ? 'Add New Brand Logo' : 'Select Brand Logo for Catalog PDF'}
          </Typography>
          <IconButton onClick={handleCloseLogoDialog} size="small" sx={{ color: '#6A7282' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 1, maxHeight: '420px', overflowY: 'auto' }}>
          {isAddingNewLogo ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 3, fontFamily: 'var(--font-montserrat-regular)', color: '#6A7282' }}>
                Upload your brand logo. It will be added to your profile brand logos and selected automatically.
              </Typography>

              {newLogoError && (
                <Alert severity="error" sx={{ mb: 2, fontFamily: 'var(--font-montserrat-medium)', fontSize: '12px' }}>
                  {newLogoError}
                </Alert>
              )}

              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Brand Name"
                    variant="outlined"
                    value={newBrandName}
                    onChange={(e) => setNewBrandName(e.target.value)}
                    placeholder="Enter brand name"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { fontFamily: 'var(--font-montserrat-medium)', fontSize: '14px' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description (Optional)"
                    variant="outlined"
                    multiline
                    rows={2}
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="Brief description of the brand"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { fontFamily: 'var(--font-montserrat-medium)', fontSize: '14px' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    onClick={() => document.getElementById('dialog-logo-file-input').click()}
                    sx={{
                      p: 3,
                      border: '2px dashed #E5E7EB',
                      borderRadius: '12px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: '#F9FAFB',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#6318C6',
                        bgcolor: '#F3F0FF'
                      }
                    }}
                  >
                    <input
                      type="file"
                      id="dialog-logo-file-input"
                      style={{ display: 'none' }}
                      accept="image/*"
                      onChange={handleLogoFileSelect}
                    />
                    {newLogoFile ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, color: '#101828' }}>
                          Selected File:
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'var(--font-montserrat-medium)', color: '#6318C6', display: 'block', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {newLogoFile.name} ({(newLogoFile.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'var(--font-montserrat-medium)', color: '#4B5563' }}>
                          Click to select logo image
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'var(--font-montserrat-regular)', color: '#9CA3AF', display: 'block', mt: 0.5 }}>
                          Supports JPG, PNG, WebP up to 6MB
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 3, fontFamily: 'var(--font-montserrat-regular)', color: '#6A7282' }}>
                Select the brand logo you want to show on the top-left of the PDF catalogue header.
              </Typography>

              {logosLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography sx={{ fontFamily: 'var(--font-montserrat-medium)', color: '#6A7282' }}>
                    Loading brand logos...
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>

                  {/* Render Active Merchant Brand Logos */}
                  {brandLogos.map((logo) => (
                    <Grid item xs={12} sm={6} key={logo.id}>
                      <Box
                        onClick={() => setSelectedLogoId(logo.id)}
                        sx={{
                          p: 2,
                          borderRadius: '12px',
                          border: selectedLogoId === logo.id ? '2px solid #6318C6' : '1px solid #E5E7EB',
                          bgcolor: selectedLogoId === logo.id ? '#F3F0FF' : '#fff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          height: '100%',
                          minHeight: '70px',
                          boxSizing: 'border-box',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                          '&:hover': {
                            borderColor: '#6318C6',
                            bgcolor: '#F3F0FF'
                          }
                        }}
                      >
                        <Box sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: '8px', overflow: 'hidden', border: '1px solid #A33236', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
                          {logo.brand_logo_path ? (
                            <SecureImage src={getLogoUrl(logo.brand_logo_path)} alt={logo.brand_name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                          ) : (
                            <Typography variant="caption" fontSize="9px">Logo</Typography>
                          )}
                        </Box>
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Typography noWrap variant="subtitle2" sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, color: '#101828', fontSize: '14px' }}>
                            {logo.brand_name}
                          </Typography>
                          {logo.description && (
                            <Typography noWrap variant="caption" sx={{ fontFamily: 'var(--font-montserrat-regular)', color: '#6A7282', fontSize: '11px', display: 'block' }}>
                              {logo.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  ))}

                  {/* Add New Brand Logo card */}
                  <Grid item xs={12} sm={6}>
                    <Box
                      onClick={() => setIsAddingNewLogo(true)}
                      sx={{
                        p: 2,
                        borderRadius: '12px',
                        border: '1px dashed #6318C6',
                        bgcolor: 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        height: '100%',
                        minHeight: '70px',
                        boxSizing: 'border-box',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#6318C6',
                          bgcolor: '#F3F0FF'
                        }
                      }}
                    >
                      <Box sx={{ width: 40, height: 40, borderRadius: '8px', bgcolor: '#F3F0FF', border: '1px dashed #6318C6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6318C6' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1 }}>+</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, color: '#6318C6', fontSize: '14px' }}>
                          Add New Brand Logo
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'var(--font-montserrat-regular)', color: '#6A7282', fontSize: '11px' }}>
                          Upload a new brand logo
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1, pt: 3 }}>
          {isAddingNewLogo ? (
            <>
              <Button
                onClick={() => setIsAddingNewLogo(false)}
                sx={{
                  fontFamily: 'var(--font-montserrat-semibold)',
                  fontWeight: 600,
                  color: '#4B5563',
                  textTransform: 'none'
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleAddNewLogoSubmit}
                disabled={newLogoUploading}
                sx={{
                  borderRadius: '8px',
                  background: PRIMARY_GRADIENT,
                  fontFamily: 'var(--font-montserrat-semibold)',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  boxShadow: '0 4px 6px rgba(99, 24, 198, 0.2)',
                  '&:hover': {
                    background: PRIMARY_GRADIENT,
                    opacity: 0.9
                  }
                }}
              >
                {newLogoUploading ? 'Uploading Logo...' : 'Upload & Select Logo'}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleCloseLogoDialog}
                sx={{
                  fontFamily: 'var(--font-montserrat-semibold)',
                  fontWeight: 600,
                  color: '#4B5563',
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleDownloadPdf(selectedLogoId)}
                disabled={downloadingPdf || logosLoading || !selectedLogoId}
                sx={{
                  borderRadius: '8px',
                  background: PRIMARY_GRADIENT,
                  fontFamily: 'var(--font-montserrat-semibold)',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  boxShadow: '0 4px 6px rgba(99, 24, 198, 0.2)',
                  '&:hover': {
                    background: PRIMARY_GRADIENT,
                    opacity: 0.9
                  }
                }}
              >
                {downloadingPdf ? 'Generating PDF...' : 'Download PDF'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Scroll-to-top FAB */}
      {showScrollTop && (
        <Fab
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          size="medium"
          sx={{
            position: 'fixed',
            bottom: { xs: 120, sm: 32 },
            right: { xs: 'auto', sm: 24 },
            left: { xs: 16, sm: 'auto' },
            background: 'linear-gradient(135deg, #6318C6 0%, #9B59B6 100%)',
            color: '#fff',
            boxShadow: '0 4px 14px rgba(99,24,198,0.35)',
            zIndex: 1300,
            '&:hover': { opacity: 0.9, background: 'linear-gradient(135deg, #6318C6 0%, #9B59B6 100%)' },
          }}
        >
          <KeyboardArrowUp />
        </Fab>
      )}
    </>
  );
};

export default Gallery;
